import React, { useCallback } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import { SENSITIVE_DATA_CLASS } from '@rover/constants/src/platform/sensitiveData.constants';
import FocusOutline from '@rover/kibble/styles/FocusOutline';

const assignRef = (ref: InputRef, node: HTMLElement | null): void => {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    // eslint-disable-next-line no-param-reassign
    ref.current = node;
  }
};

const StyledChild = ({ children, ...props }): React.ReactElement => {
  // styled-components forwards its own `ref` via these props. In React 19 `ref`
  // is a regular prop, so spreading `props` onto the cloned child would clobber
  // the child's original ref. Merge them so both refs receive the node.
  const { ref: styledRef, ...rest } = props;
  const childRef = children.props?.ref;
  // React re-attaches a ref whose identity changes, so a new function per render loops any ref that sets state.
  const mergedRef = useCallback(
    (node: HTMLElement | null): void => {
      assignRef(childRef, node);
      assignRef(styledRef, node);
    },
    [childRef, styledRef]
  );
  return React.cloneElement(children, { ...rest, ref: mergedRef });
};

export const Wrapper = styled(StyledChild)`
  height: 40px;
  width: ${(props) => props.width ?? '100%'};
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: ${({ theme }) => theme.radii.secondary};
  border: ${({ theme }) => theme.borderWidth.primary} solid
    ${(props) =>
      props.haserror === 'true'
        ? `${props.theme.colors.border.error} !important`
        : props.theme.colors.border.primary};
  color: ${({ theme }) => theme.textColor.primary};
  display: block;
  padding: ${({ theme }) => theme.space['2x']};
  transition: border-color ease-in-out 0.15s;

  &:focus {
    ${(props) =>
      props.haserror === 'true'
        ? FocusOutline({ outlineOffset: 0 })
        : FocusOutline({ outlineOffset: 2 })};
  }
  &:focus-within {
    ${(props) =>
      props.haserror === 'true'
        ? FocusOutline({ outlineOffset: 0 })
        : FocusOutline({ outlineOffset: 2 })};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.background.interactive.disabled};
    border: 2px solid ${({ theme }) => theme.colors.interactive_border.disabled};
    cursor: not-allowed;
    opacity: 1;
  }
`;

type InputRef = any;
// type InputRef = React.RefObject<HTMLInputElement>;

type InputProps = {
  className: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  ref?: InputRef;
};

export type Props = {
  children: (InputProps) => React.ReactNode;
  inputRef?: InputRef;
  onChange?: (value: string) => void;
  sensitive?: boolean;
  hasError?: boolean;
  className?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  placeholder?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  name?: string;
  width?: string;
};

const getClassNames = (sensitive, className) =>
  classNames(className, {
    [SENSITIVE_DATA_CLASS]: sensitive,
  });

const AsInput = ({
  children,
  className = '',
  inputRef,
  onChange,
  sensitive = false,
  hasError,
  width,
}: Props): JSX.Element => {
  const inputProps: InputProps = {
    className: getClassNames(sensitive, className),
    onChange: (event) => onChange && onChange(event.target.value),
    ref: inputRef,
  };

  return (
    <Wrapper className={getClassNames(sensitive, className)} haserror={`${hasError}`} width={width}>
      {children(inputProps)}
    </Wrapper>
  );
};

export default AsInput;
